import { Plugin } from '@ckeditor/ckeditor5-core'
import { Widget, toWidget, toWidgetEditable, viewToModelPositionOutsideModelElement } from '@ckeditor/ckeditor5-widget'
import InsertTagsCommand from './inserttagscommand'

import './tags.css';

export default class TagsEditing extends Plugin {
    static get requires() {
        return [Widget]
    }

    init() {
        this._defineSchema()
        this._defineConverters()

        this.editor.commands.add('insertTags', new InsertTagsCommand(this.editor))

        this.editor.editing.mapper.on(
            'viewToModelPosition',
            viewToModelPositionOutsideModelElement(this.editor.model, viewElement => viewElement.hasClass('custom-tags-title'))
        )

        this.editor.config.define('tagsConfig', {
            types: []
        })

        this.listenTo(this.editor.editing.view.document, 'keydown', (evt, data) => {
            // Only handle Down or Up
            if (!this._isArrowDown(data.keyCode) && !this._isArrowUp(data.keyCode)) {
                return;
            }

            // return early if not a tags element
            const element = this.editor.model.document.selection.getSelectedElement();
            if (!element?.is('element', 'tags')) {
                return;
            }

            // return early if no view element
            const viewEl = this.editor.editing.mapper.toViewElement(element);
            if (!viewEl) {
                return;
            }

            if (this._shouldMoveInsideTags(data.keyCode, viewEl)) {
                this._moveCursorToTagsContent(element);
                data.preventDefault();
                evt.stop();
            }
        }, { priority: 'high' });

        this.listenTo(this.editor.editing.view.document, 'enter', (evt, data) => {
            if (data.isSoft) {
                return;
            }

            const model = this.editor.model;
            const selection = model.document.selection;
            const position = selection.getFirstPosition();

            let parentTag = null;
            let current = position.parent;

            while (current) {
                if (current.name === 'tags') {
                    parentTag = current;
                    break;
                }
                current = current.parent;
            }


            if (parentTag) {
                data.preventDefault();
                evt.stop();

                this.editor.model.change(writer => {
                    const newParagraph = writer.createElement('paragraph');
                    this.editor.model.insertContent(newParagraph, this.editor.model.createPositionAt(parentTag, 'after'));
                    writer.setSelection(writer.createPositionAt(newParagraph, 0));
                });
            }
        }, { priority: 'high' });
    }

    _defineSchema() {
        const schema = this.editor.model.schema

        schema.register('tags', {
            inheritAllFrom: '$blockObject',
            allowAttributes: ['tag']
        })

        schema.register('tagsInner', {
            isLimit: true,
            allowIn: 'tags',
            allowContentOf: '$block',
            allowAttributes: ['tag']
        })

        schema.register('tagsData', {
            isLimit: true,
            allowIn: 'tagsInner',
            allowContentOf: '$block',
            allowAttributes: ['tag']
        })

        schema.register('tagsTitle', {
            isLimit: true,
            allowIn: 'tagsInner',
            allowContentOf: '$block',
            allowAttributes: ['tag']
        })

        schema.register('tagsContent', {
            isLimit: true,
            allowIn: 'tagsInner',
            allowContentOf: '$root'
        })

        schema.addChildCheck((context, childDefinition) => {
            if (context.endsWith('tagsContent') && childDefinition.name == 'tags') {
                return false
            }
        })
    }

    _defineConverters() {
        const conversion = this.editor.conversion

        conversion.for('upcast').elementToElement({
            model: 'tags',
            view: {
                name: 'section',
                classes: 'custom-tags'
            }
        })

        conversion.for('dataDowncast').elementToElement({
            model: 'tags',
            view: {
                name: 'section',
                classes: 'custom-tags'
            }
        })

        conversion.for('editingDowncast').elementToElement({
            model: 'tags',
            view: (modelElement, { writer: viewWriter }) => {
                const section = viewWriter.createContainerElement('section', { class: 'custom-tags' })

                return toWidget(section, viewWriter, { label: 'custom tags widget' })
            }
        })

        conversion.for('upcast').elementToElement({
            model: 'tagsInner',
            view: {
                name: 'div',
                classes: 'custom-tags-inner'
            }
        })

        conversion.for('dataDowncast').elementToElement({
            model: 'tagsInner',
            view: (modelItem, { writer: viewWriter }) => createInnerContainer(modelItem, viewWriter)
        })

        conversion.for('editingDowncast').elementToElement({
            model: 'tagsInner',
            view: (modelElement, { writer: viewWriter }) => {
                const widgetElement = createInnerContainer(modelElement, viewWriter)

                return toWidget(widgetElement, viewWriter)
            }
        })

        conversion.for('upcast').elementToElement({
            model: 'tagsData',
            view: {
                name: 'span',
                classes: 'custom-tags-data'
            }
        })

        conversion.for('dataDowncast').elementToElement({
            model: 'tagsData',
            view: (modelItem, { writer: viewWriter }) => createDataContainer(modelItem, viewWriter)
        })

        conversion.for('editingDowncast').elementToElement({
            model: 'tagsData',
            view: (modelElement, { writer: viewWriter }) => {
                const widgetElement = createDataContainer(modelElement, viewWriter)

                return toWidget(widgetElement, viewWriter)
            }
        })

        conversion.for('upcast').elementToElement({
            model: 'tagsTitle',
            view: {
                name: 'h4',
                classes: 'custom-tags-title'
            },
        })

        conversion.for('dataDowncast').elementToElement({
            model: 'tagsTitle',
            view: (modelItem, { writer: viewWriter }) => createPlaceholderView(modelItem, viewWriter)
        })

        conversion.for('editingDowncast').elementToElement({
            model: 'tagsTitle',
            view: (modelItem, { writer: viewWriter }) => {
                const widgetElement = createPlaceholderView(modelItem, viewWriter)

                return toWidget(widgetElement, viewWriter)
            }
        })

        conversion.for('upcast').elementToElement({
            model: 'tagsContent',
            view: {
                name: 'div',
                classes: 'custom-tags-content'
            }
        })

        conversion.for('dataDowncast').elementToElement({
            model: 'tagsContent',
            view: {
                name: 'div',
                classes: 'custom-tags-content'
            }
        })

        conversion.for('editingDowncast').elementToElement({
            model: 'tagsContent',
            view: (modelItem, { writer: viewWriter }) => {
                const div = viewWriter.createEditableElement('div', {
                    class: 'custom-tags-content'
                })

                return toWidgetEditable(div, viewWriter)
            }
        })

        function createInnerContainer(modelItem, viewWriter) {
            let tagData = {}

            if (modelItem.getAttribute('tag')) {
                tagData = {
                    'id': modelItem.getAttribute('tag').id,
                    'color': modelItem.getAttribute('tag').color,
                }
            } else {
                const tagsSplit = modelItem.getChild(0).getChild(0).data.split('|')

                tagData = {
                    'id': tagsSplit[0],
                    'color': tagsSplit[1],
                }
            }

            const innerContainer = viewWriter.createContainerElement('div', {
                class: 'custom-tags-inner',
                'data-tagId': tagData.id,
            })

            if (tagData.color) {
                viewWriter.setStyle({
                    'border-left': '5px solid ' + tagData.color,
                    'padding-left': '10px',
                }, innerContainer)
            }

            return innerContainer
        }

        function createDataContainer(modelItem, viewWriter) {
            const tagColor = modelItem.getAttribute('tag') ? modelItem.getAttribute('tag').color : ''
            const tagId = modelItem.getAttribute('tag') ? modelItem.getAttribute('tag').id : ''

            const dataContainer = viewWriter.createContainerElement('span', {
                class: 'custom-tags-data',
                style: 'display: none',
            })

            const innerText = viewWriter.createText(tagId + '|' + tagColor);
            viewWriter.insert(viewWriter.createPositionAt(dataContainer, 0), innerText)

            return dataContainer
        }

        function createPlaceholderView(modelItem, viewWriter) {
            const tag = modelItem.getAttribute('tag') ? modelItem.getAttribute('tag').name : ''

            const placeholderView = viewWriter.createContainerElement('h4', {
                class: 'custom-tags-title',
            })

            const innerText = viewWriter.createText(tag);
            viewWriter.insert(viewWriter.createPositionAt(placeholderView, 0), innerText)

            return placeholderView
        }
    }

    _isArrowDown(keyCode) {
        return keyCode === 40;
    }

    _isArrowUp(code) {
        return code === 38;
    }

    _moveCursorToTagsContent(element) {
        const inner = element.getChild(0);
        const tagsContent = Array.from(inner?.getChildren() || [])
            .find(child => child.name === 'tagsContent');

        if (tagsContent) {
            this.editor.model.change(writer => {
                writer.setSelection(writer.createPositionAt(tagsContent, 'end'));
            });
        }
    }

    _shouldMoveInsideTags(code, viewEl) {
        const atTop = viewEl.hasClass('ck-widget_type-around_show-fake-caret_before');
        const atBottom = viewEl.hasClass('ck-widget_type-around_show-fake-caret_after');

        return (this._isArrowUp(code) && atBottom) ||
            (this._isArrowDown(code) && !atTop && !atBottom);
    }
}
